import { useTranslation } from '@mukil/i18n';
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@jumpstone/ui-react';
import { useSession } from '../../data/use_session';
import { EmployeeLocaleSwitcher } from './employee_locale_switcher';
import { EmployeeNotificationBell } from './employee_notification_bell';
import { active_ess_tab } from './nav_model';
import { navigate, route_to_path } from '../../lib/router';
import type { Route } from '../../lib/router';
import { SignOutMenuItem } from './sign_out_menu_item';
import { WageProAppIcon } from './wagepro_app_icon';

/**
 * The pared-back app bar an employee sees in the self-service view.
 *
 * Reads the signed-in name from `useSession()` (the real session), NOT from
 * `useMyPay()` (C7 PR B, #21938) — the avatar's name has nothing to do with
 * payroll data, and calling the now-network-backed `useMyPay` here purely
 * for a display name would fire a second, duplicate
 * `listEmployerLinks`/`listPayslips` fetch alongside `MyPayScreen`'s own
 * call. This leaves `MyPayScreen` as `useMyPay`'s only caller.
 *
 * The avatar is a real menu, not a decorative initial: this bar is
 * the ONLY chrome an employee ever sees — `TopNav`/`AccountMenu` belong to
 * the admin view, and `MobileNavDrawer` carries module navigation only — so
 * without a sign-out here an employee (the phone/Capacitor-shell persona)
 * had no way to sign out of the app at all.
 */
export function EmployeeTopBar({ route }: { route: Route }): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { session, toggle_role, can_switch_to_admin } = useSession();
  // Shared with `App.tsx`'s phone strip (`nav_model.ts`) — one resolver, so
  // the two navigations cannot disagree about which page is current. Also
  // fixes the fallback routes (`#/`, an employer hash in the shell) where
  // comparing `route.module === 'my_pay'` marked NEITHER link current even
  // though My Pay was what rendered.
  const active_tab = active_ess_tab(route);

  const switch_to_admin = (): void => {
    toggle_role();
    navigate({ module: 'dashboard' });
  };
  const switch_to_admin_label = t('account.switch_to_admin');

  return (
    // Unlike `TopNav` (admin), this bar has no search box or module nav to
    // drop on narrow viewports; below `lg` it hides only the language
    // switcher and (for anyone who cannot switch to admin) the avatar menu,
    // both of which the Profile tab carries there. `flex-wrap` (a Tailwind
    // utility; `.wp-topbar` never sets `flex-wrap` itself, so there's no
    // unlayered-CSS conflict, same reasoning as the hamburger wrapper in
    // TopNav) lets the trailing action group drop to its own row instead of
    // overflowing the viewport horizontally when it doesn't all fit.
    <header className="wp-topbar flex-wrap">
      <WageProAppIcon
        alt={t('brand.logo_alt')}
        className="wp-topbar-mark shrink-0"
      />
      <span className="wp-wordmark">{t('common.app_name')}</span>
      {/* "My Pay" is redundant next to the wordmark once space is tight —
          drop it below `lg` (phone AND tablet) to leave room for the
          trailing action group; only desktop keeps it, exactly as before. */}
      {/* ESS module links — visible on lg+ only, same space-budget reasoning
          as the "My Pay" label that used to live here. The active link uses
          the accent colour; inactive links use ink-muted so the distinction
          is clear without needing a separate active-indicator element. */}
      <nav
        aria-label={t('my_leave.ess_nav_label')}
        className="hidden lg:flex items-center gap-4 ms-3"
      >
        <a
          href={route_to_path({ module: 'my_pay' })}
          className={`text-[13.5px] font-semibold ${
            active_tab === 'my_pay'
              ? 'text-[color:var(--accent)]'
              : 'text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]'
          }`}
          aria-current={active_tab === 'my_pay' ? 'page' : undefined}
        >
          {t('my_pay.title')}
        </a>
        <a
          href={route_to_path({ module: 'my_leave' })}
          className={`text-[13.5px] font-semibold ${
            active_tab === 'my_leave'
              ? 'text-[color:var(--accent)]'
              : 'text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]'
          }`}
          aria-current={active_tab === 'my_leave' ? 'page' : undefined}
        >
          {t('my_leave.title')}
        </a>
        <a
          href={route_to_path({ module: 'my_profile' })}
          className={`text-[13.5px] font-semibold ${
            active_tab === 'my_profile'
              ? 'text-[color:var(--accent)]'
              : 'text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]'
          }`}
          aria-current={active_tab === 'my_profile' ? 'page' : undefined}
        >
          {t('my_profile.title')}
        </a>
      </nav>

      <div className="ms-auto flex items-center gap-1.5 lg:gap-2">
        {/* The bell sits in the chrome, not on a screen, so a decision
            reaches an employee who is looking at My Pay — see the
            component's own docblock. */}
        <EmployeeNotificationBell />
        {/* Desktop only. On a phone/tablet the employee surface's Profile tab
            holds the language choice (and sign-out), so the phone app bar is
            the design's wordmark + bell and nothing else. */}
        <span className="hidden lg:inline-flex">
          <EmployeeLocaleSwitcher />
        </span>
        {/* Two tiers for the "switch to admin" action in the bar itself,
            matching the same space-constrained reasoning as the "My Pay"
            label above:
             - Tablet (sm to lg): icon + label together. There's room for a
               real button here but not the spare width desktop has once
               "My Pay" is back — an icon keeps the target recognizable at a
               glance without the full label's width.
             - Desktop (lg+): label-only, exactly as before.
            Mobile (< sm) deliberately has NO button here. It used to carry an
            icon-only tier, on the reasoning that the trailing group (locale
            switcher + button + avatar) cannot fit a ~150px label on a 320px
            viewport inside `.wp-topbar`'s 14px side padding. That width
            argument still holds — the conclusion was wrong; see the
            profile-menu row below for why. The icon itself is a dashboard
            grid, since the action lands on the admin dashboard — now shared
            by the tablet button and that menu row. */}
        {/* Employer-only. This is the "preview as employee" escape hatch for
            someone who HAS an admin view; a real employee has none, so
            offering it would either dump them into an admin shell whose every
            query 403s (their grant carries `firmId: null` and no
            `payroll:employer:*` scope) or read as a privilege they do not
            have. Both tiers are gated together — dropping one would leave
            the action reachable at that breakpoint only. `set_role` refuses
            the switch independently (`wagepro_store.tsx`), so this is the
            affordance half of a two-layer guard. The mobile dropdown row
            below carries the same gate. */}
        {can_switch_to_admin && (
          <>
            <span className="hidden sm:inline-flex lg:hidden">
              <Button variant="secondary" size="sm" onClick={switch_to_admin}>
                <AdminDashboardIcon />
                {switch_to_admin_label}
              </Button>
            </span>
            <span className="hidden lg:inline-flex">
              <Button variant="secondary" size="sm" onClick={switch_to_admin}>
                {switch_to_admin_label}
              </Button>
            </span>
          </>
        )}
        {/* The avatar menu: desktop always; on a phone/tablet only for an
            employer who can switch to the admin view — for everyone else
            its one remaining action, sign out, lives on the Profile tab. */}
        <span
          className={
            can_switch_to_admin ? 'inline-flex' : 'hidden lg:inline-flex'
          }
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="wp-avatar-button rounded-full"
                aria-label={t('account.open')}
              >
                <Avatar name={session.name} colorVariant="teal" size="sm" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuLabel>{session.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Below `sm` this is the ONLY route to the admin view: the bar's
                two labelled tiers are `sm:`+ and `lg:`+, and the icon-only
                mobile tier they used to sit beside was removed. A bare
                four-square glyph in the tightest corner of the tightest
                viewport read as decoration, and its label existed only as
                `aria-label` plus a hover tooltip — neither of which a touch
                user can discover, so on a phone the action was effectively
                invisible (reported on a real device, 2026-08-27). A named
                menu row alongside "Sign out" is self-describing and costs no
                header width. */}
              {can_switch_to_admin && (
                <>
                  <DropdownMenuItem
                    className="sm:hidden"
                    onSelect={switch_to_admin}
                  >
                    <AdminDashboardIcon />
                    {switch_to_admin_label}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="sm:hidden" />
                </>
              )}
              <SignOutMenuItem />
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>
    </header>
  );
}

/** Dashboard-grid glyph for the "switch to admin" action — shared by the
 * icon-only (mobile) and icon+label (tablet) button tiers above. */
function AdminDashboardIcon(): React.JSX.Element {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}
